import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BrowseLayout from '../../components/home/BrowseLayout';
import { LEGAL_ENTITY, LEGAL_STATE, LEGAL_EMAIL, EFFECTIVE_DATE } from './legalInfo';
import './LegalPage.css';

const Mail = () => <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a>;

// Privacy Policy. Written from what the site actually collects as of the
// effective date (Google sign-in email, PayPal checkout shipping details,
// PostHog analytics and session replay, server logs with IP addresses, SendGrid
// order emails, Azure hosting). Update it when any of that changes. Draft
// prepared without a lawyer; have counsel review before relying on it.
const PrivacyPage = () => {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <BrowseLayout>
            <article className="legal-doc">
                <h1>Privacy Policy</h1>
                <p className="legal-effective">Effective {EFFECTIVE_DATE}</p>

                <div className="legal-callout">
                    <p><strong>The short version.</strong></p>
                    <ul>
                        <li>We collect what we need to sell and ship you cards: your email, your shipping details and your orders.</li>
                        <li>PayPal handles payments. We never see or store your full card or bank numbers.</li>
                        <li>We use analytics, including recordings of how visitors use the site, to find bugs and improve it.</li>
                        <li>We don't sell your personal information or use it for targeted advertising.</li>
                        <li>Email <Mail /> to see, correct or delete your information.</li>
                    </ul>
                </div>

                <p>
                    This policy explains how {LEGAL_ENTITY} ("Gradies," "we," "us") collects, uses and shares
                    information when you use gradies.com (the "Site"). It's part of our{' '}
                    <Link to="/terms">User Agreement</Link>.
                </p>

                <h2>1. Information We Collect</h2>
                <h3>Information you give us</h3>
                <ul>
                    <li>
                        <strong>Account information.</strong> When you sign in with Google, Google shares your email
                        address and an account identifier with us. We don't receive your Google password.
                    </li>
                    <li>
                        <strong>Order and shipping information.</strong> When you buy, we receive your name, shipping
                        address, phone number and email from the PayPal checkout, plus the details of your order.
                    </li>
                    <li>
                        <strong>Messages.</strong> If you email us, we keep the message and your contact details so we
                        can respond.
                    </li>
                </ul>
                <h3>Payment information</h3>
                <p>
                    Payments are handled by PayPal. We receive confirmation that you paid and the related
                    transaction details, but not your full card or bank account numbers. PayPal's use of your
                    information is covered by PayPal's privacy statement.
                </p>
                <h3>Information collected automatically</h3>
                <ul>
                    <li>
                        <strong>Usage and device data.</strong> Pages you view, searches, clicks, the browser and device
                        you use, your approximate location based on your IP address, and the site that referred you.
                    </li>
                    <li>
                        <strong>Session recordings.</strong> We use PostHog to record how visitors interact with the
                        Site, such as mouse movements, clicks, scrolling and page changes, so we can find bugs and
                        improve the experience. When you're signed in, recordings are linked to your email address.
                    </li>
                    <li>
                        <strong>Server logs.</strong> Our servers log requests to the Site, including IP addresses and
                        the time and page requested, for security and troubleshooting.
                    </li>
                </ul>
                <h3>Cookies and local storage</h3>
                <p>
                    We use cookies and your browser's local storage to keep you signed in and to run analytics.
                    These include a secure sign-in cookie that keeps your session active and cookies set by PostHog.
                    You can block or delete cookies in your browser settings, but you may then be signed out or some
                    features may not work.
                </p>

                <h2>2. How We Use Information</h2>
                <ul>
                    <li>to process, ship and support your orders, including issuing refunds;</li>
                    <li>to send you emails about your orders, such as confirmations and tracking;</li>
                    <li>to run your account and keep it secure;</li>
                    <li>to understand how the Site is used, fix problems and improve it;</li>
                    <li>to prevent fraud and abuse and enforce our User Agreement; and</li>
                    <li>to comply with legal obligations, such as tax and accounting records.</li>
                </ul>
                <p>We don't use your information for targeted advertising, and we don't send marketing emails.</p>

                <h2>3. How We Share Information</h2>
                <p>We share information only as needed to run the Site and fill your orders:</p>
                <ul>
                    <li><strong>Brokers</strong> authorized by Gradies receive the order and shipping details needed to ship your card.</li>
                    <li><strong>Service providers</strong> that work on our behalf: Google (sign-in), PayPal (payments), PostHog (analytics and session recordings), Twilio SendGrid (order emails), Microsoft Azure (hosting and storage) and shipping carriers.</li>
                    <li><strong>Legal and safety.</strong> When required by law or legal process, or to protect the rights, property or safety of Gradies, our customers or others.</li>
                    <li><strong>Business transfers.</strong> If Gradies is involved in a merger, sale or reorganization, information may transfer as part of that deal, under this policy's protections.</li>
                </ul>
                <p>
                    We don't sell your personal information, and we don't share it for cross-context behavioral
                    advertising.
                </p>

                <h2>4. How Long We Keep Information</h2>
                <p>
                    We keep order records for as long as needed to support your orders and to meet tax, accounting and
                    legal requirements. We keep account information while your account is active. Analytics data and
                    session recordings are kept for a limited period and then deleted. When we no longer need
                    information, we delete it or make it anonymous.
                </p>

                <h2>5. Security</h2>
                <p>
                    We use reasonable safeguards to protect your information, including encrypted connections and
                    access controls. No method of transmission or storage is completely secure, so we can't guarantee
                    absolute security. If we learn of a breach affecting your personal information, we'll notify you
                    as the law requires.
                </p>

                <h2>6. Your Choices and Rights</h2>
                <p>
                    You can ask to see, correct or delete the personal information we hold about you, or to get a copy
                    of it, by emailing <Mail />. We'll verify your request, usually by confirming it came from the email
                    on your account, and respond within the time the law requires. We may keep some information when
                    the law requires it, such as order records. We won't treat you differently for making a request.
                </p>
                <p>
                    Depending on where you live, including {LEGAL_STATE}, you may have additional rights under state
                    privacy laws, such as the right to opt out of certain processing or to appeal a decision about your
                    request. To use any of these rights, or to appeal, email <Mail />.
                </p>

                <h2>7. Children</h2>
                <p>
                    The Site isn't directed to children under 13, and we don't knowingly collect their personal
                    information. You must be 18 or older to buy. If you believe a child has given us personal
                    information, email <Mail /> and we'll delete it.
                </p>

                <h2>8. Changes to This Policy</h2>
                <p>
                    We may update this policy from time to time. We'll post the new version here with a new effective
                    date, and for significant changes we'll take reasonable steps to let you know, such as a notice on
                    the Site.
                </p>

                <h2>9. Contact</h2>
                <p>
                    {LEGAL_ENTITY}<br />
                    {LEGAL_STATE}<br />
                    Email: <Mail />
                </p>
            </article>
        </BrowseLayout>
    );
};

export default PrivacyPage;
